// Helpers shared by the Users cluster.
//
// The `planColors` / `statusColors` class maps that used to live here are gone:
// they were the `rounded-full px-2 py-0.5 bg-*-100 text-*-700` pill spellings
// that `Badge` owns now. What is left is the mapping from a domain value to a
// Badge *tone*, which is the only part that was ever page knowledge —
// emerald/yellow folded onto the green/amber roles from the palette contract.
import type { BadgeTone } from '../../components/ui'
import type { UserListParams } from '../../api/client'

export function extractApiError(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error
}

export const PLANS = ['free', 'paid'] as const
export type Plan = typeof PLANS[number]

export const planTone: Record<Plan, BadgeTone> = {
  free: 'neutral',
  paid: 'success',
}

export const statusTone: Record<string, BadgeTone> = {
  active: 'success',
  pending: 'warning',
  deactivated: 'neutral',
}

export const supportedLanguages = [
  { code: 'en', label: 'English' },
  { code: 'tr', label: 'Türkçe' },
]


export type SortKey = NonNullable<UserListParams['sort']>

/** `users.planFree` / `users.planPaid`, `users.statusActive` … */
export function capKey(prefix: string, value: string) {
  return `${prefix}${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

export function formatDate(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatTimeAgo(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return t('users.timeJustNow')
  if (minutes < 60) return t('users.timeMinutesAgo', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('users.timeHoursAgo', { count: hours })
  const days = Math.floor(hours / 24)
  if (days < 30) return t('users.timeDaysAgo', { count: days })
  return formatDate(dateStr)
}
